import React from 'react';
import { Box, Button, Chip, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import { LOG_TYPES, getLogTypeByCategory } from '../../constants/logTypeRegistry';
import { getCaptureFirstTimelineContext } from './utils/captureFirstTimelineContext';
import { getMedicationReviewClarification } from './utils/medicationReviewClarification';
import SourceOriginalNotePanel from './SourceOriginalNotePanel';
import TimelineClarificationSection from './TimelineClarificationSection';

const TEXT_PRIMARY = '#1F2937';
const TEXT_MUTED = '#6B7280';
const REVIEW_AMBER = colors.semantic.warning || '#F59E0B';
const DEFAULT_BUCKET_PALETTE = LOG_TYPES.log.palette;

const BUCKET_TYPE_CATEGORY_MAP = {
  medication_context: 'medication',
  medication: 'medication',
  meds: 'medication',
  med: 'medication',
  food: 'food',
  meal: 'food',
  meals: 'food',
  dinner: 'food',
  lunch: 'food',
  breakfast: 'food',
  snack: 'food',
  activity: 'activity',
  play: 'activity',
  played: 'activity',
  outside: 'activity',
  behavior: 'behavior',
  behavioural: 'behavior',
  mood: 'mood',
  sleep: 'sleep',
  bathroom: 'bathroom',
  toileting: 'bathroom',
  health: 'health',
  sensory: 'health',
  milestone: 'milestone',
};

const normalizeText = (value) => String(value || '').trim();
const normalizeKeyText = (value = '') => normalizeText(value)
  .toLowerCase()
  .replace(/[^\w\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getClarificationAction = ({
  entryData = {},
  entryConfig = {},
  parentCategoryKey = '',
  onAddDetails = null,
  onReviewMedication = null,
  onReviewProvider = null,
}) => {
  const normalizedKind = normalizeText(
    entryData.kind
    || entryData.type
    || entryData.category
    || entryConfig.kind
    || entryConfig.type
    || entryConfig.category
    || parentCategoryKey
  ).toLowerCase();

  const isMedicationReview = normalizedKind === 'medication'
    || normalizedKind === 'medication_change'
    || normalizedKind === 'meds'
    || Boolean(entryData?.metadata?.medicationAdministrationReview)
    || Boolean(entryData?.metadata?.medicationReview);
  const isDoctorVisitReview = normalizedKind === 'doctor_visit'
    && !entryData.providerId;

  if (isDoctorVisitReview && typeof onReviewProvider === 'function') {
    return {
      label: 'Review provider',
      onClick: onReviewProvider,
    };
  }

  if (isMedicationReview && typeof onReviewMedication === 'function') {
    return {
      label: 'Review medication',
      onClick: onReviewMedication,
    };
  }

  if (isMedicationReview) {
    return {
      label: '',
      onClick: null,
    };
  }

  return {
    label: 'Add more details',
    onClick: onAddDetails,
  };
};

export const formatReviewReasonForCaregiver = (reason = '') => {
  const text = normalizeText(reason);
  if (!text) return '';

  if (
    /\b(time wording|time hint|approximate time|day-part)\b/i.test(text)
    || /\btime\b.*\b(confirm|confirmation|unclear|unknown)\b/i.test(text)
  ) {
    return /\bafternoon\b/i.test(text)
      ? 'Did this happen this afternoon?'
      : 'When did this happen?';
  }

  const medicationTypoMatch = text.match(/^Medication name ['"]([^'"]+)['"] is misspelled and needs confirmation\.?$/i);
  if (medicationTypoMatch) {
    return `Please confirm the medication name: "${medicationTypoMatch[1]}"`;
  }

  const possibleTypoMatch = text.match(/^Possible typo ['"]([^'"]+)['"] for ['"]([^'"]+)['"]\.?$/i);
  if (possibleTypoMatch) {
    return `Please check this word: "${possibleTypoMatch[1]}" may mean "${possibleTypoMatch[2]}"`;
  }

  if (text === 'missing_dose') {
    return 'Please confirm the dose.';
  }

  if (text === 'exact_date_needed') {
    return 'What date did this happen?';
  }

  if (text === 'provider_unknown') {
    return 'Confirm which provider this visit was with.';
  }

  if (/^no active master medication matched this mention\.?$/i.test(text)) {
    return 'This medication is not in the master medication list yet.';
  }

  if (/^medication was mentioned without a unique master medication match\.?$/i.test(text)) {
    return 'This medication is not in the master medication list yet.';
  }

  if (/^medication name and dose need confirmation\.?$/i.test(text)) {
    return 'Please confirm this medication against the master medication list.';
  }

  if (
    /^(needs review|review required|extraction issue)$/i.test(text)
    || /\b(ai|extract(?:ion|ed)|classif(?:y|ication)|processing issue)\b/i.test(text)
  ) {
    return 'Please check this detail.';
  }

  return text.replace(/_/g, ' ');
};

export const isCaregiverConfirmationReason = (reason = '') => {
  const text = normalizeText(reason);
  if (!text) return false;
  if (/\b(observation|follow[- ]?up|advice|recommend(?:ation|ed)?|extracted fact|care strategy)\b/i.test(text)) {
    return false;
  }
  if (/^(needs review|review required|extraction issue)$/i.test(text)) return false;
  return Boolean(
    text === 'exact_date_needed'
    || text === 'provider_unknown'
    || /\b(confirm|confirmation|clarify|clarification|unclear|unknown|missing|misspelled|typo|approximate)\b/i.test(text)
    || /\b(when|what date|which|did this|is this|was this)\b.*\?/i.test(text)
    || /^no active master medication matched this mention\.?$/i.test(text)
  );
};

const REVIEW_DESTINATION_LABELS = Object.freeze({
  goal: 'Goal',
  goals: 'Goal',
  allergy: 'Allergy',
  allergies: 'Allergy',
  medication: 'Medication',
  medications: 'Medication',
  person: 'Person update',
  'person update': 'Person update',
  profile: 'Person update',
});

const normalizeReviewDestination = (value = '') => {
  const key = normalizeKeyText(value);
  return REVIEW_DESTINATION_LABELS[key] || '';
};

const inferReviewDestination = ({ reviewReasons = [], parentCategoryKey = '' } = {}) => {
  const combinedReasons = reviewReasons.join(' ');
  const parent = normalizeParentCategoryKey(parentCategoryKey);
  if (parent === 'medication' || /\bmedication|dose|prescription\b/i.test(combinedReasons)) return 'Medication';
  if (parent === 'food' || /\ballerg(?:y|ic)|food reaction\b/i.test(combinedReasons)) return 'Allergy';
  if (parent === 'goal' || /\bgoal\b/i.test(combinedReasons)) return 'Goal';
  if (/\bperson|identity|name change\b/i.test(combinedReasons)) return 'Person update';
  if (/\bdate|time|day-part|afternoon|morning|evening\b/i.test(combinedReasons)) return 'Timeline date';
  return '';
};

export const formatTimelineReviewSummary = ({
  reviewDestinations = {},
  reviewReasons = [],
  parentCategoryKey = '',
} = {}) => {
  const counts = {};
  Object.entries(reviewDestinations).forEach(([destination, count]) => {
    const label = normalizeReviewDestination(destination);
    const numericCount = Number(count);
    if (label && numericCount > 0) counts[label] = (counts[label] || 0) + numericCount;
  });
  if (!Object.keys(counts).length) {
    const inferred = inferReviewDestination({ reviewReasons, parentCategoryKey });
    if (inferred === 'Timeline date') return 'When did this happen?';
    if (inferred) counts[inferred] = 1;
  }

  const entries = Object.entries(counts);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);
  if (!total) return 'Timeline detail needs review';
  if (entries.length === 1) {
    const [[label, count]] = entries;
    if (label === 'Medication' && count === 1) return 'Medication needs review';
    const pluralLabel = count === 1 ? label : (
      label === 'Person update' ? 'Person updates' : `${label}s`
    );
    return `${count} ${pluralLabel} ${count === 1 ? 'needs' : 'need'} review`;
  }
  if (total === 2 && entries.every(([, count]) => count === 1)) {
    return `${entries[0][0]} and ${entries[1][0]} need review`;
  }
  const breakdown = entries
    .map(([label, count]) => `${count === 1 ? label : (label === 'Person update' ? 'Person updates' : `${label}s`)} ${count}`)
    .join(' · ');
  return `${total} items need review (${breakdown})`;
};

const compactComparableText = (value = '') => normalizeText(value)
  .toLowerCase()
  .replace(/[^\w\s]/g, '')
  .replace(/\b(the|a|an|and|was|were|is|are|had|has|have|got|child)\b/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const resolveBucketCategoryKey = (bucket = {}) => {
  const candidates = [
    bucket.bucketType,
    bucket.type,
    bucket.label,
    bucket.summary,
  ].map((value) => normalizeText(value).toLowerCase()).filter(Boolean);

  for (const candidate of candidates) {
    if (BUCKET_TYPE_CATEGORY_MAP[candidate] || LOG_TYPES[candidate]) {
      return BUCKET_TYPE_CATEGORY_MAP[candidate] || candidate;
    }
  }

  const haystack = candidates.join(' ');
  if (/\b(med|meds|medication|dose|dose given|bedtime meds)\b/.test(haystack)) {
    return 'medication';
  }
  if (/\b(food|meal|ate|eating|breakfast|lunch|dinner|snack|nuggets|yogurt|yoghurt|milk|crackers)\b/.test(haystack)) {
    return 'food';
  }
  if (/\b(activity|played|play|outside|walk|run|bubbles|chalk|park)\b/.test(haystack)) {
    return 'activity';
  }
  if (/\b(behavior|upset|meltdown|cried|calmed|refused|homework|math)\b/.test(haystack)) {
    return 'behavior';
  }
  if (/\b(sleep|slept|asleep|bedtime|woke|wake)\b/.test(haystack)) {
    return 'sleep';
  }
  if (/\b(mood|happy|sad|anxious|calm|irritable)\b/.test(haystack)) {
    return 'mood';
  }
  if (/\b(health|sick|symptom|sneezing|itchy|fever|pain)\b/.test(haystack)) {
    return 'health';
  }

  return 'log';
};

const shouldHideRepeatingSummary = (bucket = {}) => {
  const label = compactComparableText(bucket.label);
  const summary = compactComparableText(bucket.summary);

  if (!label || !summary) {
    return false;
  }

  return label === summary
    || summary.includes(label)
    || label.includes(summary);
};

const getVisibleBucketSummary = (bucket = {}, { allowRepeatingSummary = false } = {}) => (
  !allowRepeatingSummary && shouldHideRepeatingSummary(bucket) ? '' : normalizeText(bucket.summary)
);

const shouldHideBucketRow = (bucket = {}) => {
  const rawSummary = normalizeText(bucket.summary);
  if (!rawSummary) {
    return true;
  }

  if (getVisibleBucketSummary(bucket)) {
    return false;
  }

  return new Set(['rest', 'note', 'notes', 'detail', 'context']).has(normalizeKeyText(bucket.label));
};

const normalizeParentCategoryKey = (value = '') => {
  const normalized = normalizeKeyText(value);
  if (normalized === 'meds') return 'medication';
  return BUCKET_TYPE_CATEGORY_MAP[normalized] || normalized;
};

const resolveMedicationBucketLabel = (bucket = {}) => {
  const labelKey = normalizeKeyText(bucket.label);
  const combined = normalizeKeyText(`${bucket.label} ${bucket.bucketType} ${bucket.summary}`);

  if (/\b(initial|before|baseline)\b/.test(combined)) {
    return /\bpain\b/.test(combined) ? 'Pain before dose' : 'Before dose';
  }
  if (/\b(relief|help|helped|effect|improvement|decreased|better|after)\b/.test(combined)) {
    return 'Did it help?';
  }
  if (labelKey === 'rest' || /\b(rest|comfort|comfortable)\b/.test(combined)) {
    return 'Comfort after';
  }

  return normalizeText(bucket.label);
};

const resolveBehaviorBucketLabel = (bucket = {}) => {
  const combined = normalizeKeyText(`${bucket.label} ${bucket.bucketType} ${bucket.summary}`);

  if (/\b(trigger|before|initial|hard|happening|started)\b/.test(combined)) {
    return 'What was happening';
  }
  if (/\b(help|helped|support|intervention|response|caregiver|strategy)\b/.test(combined)) {
    return 'What helped';
  }
  if (/\b(after|recovery|resolved|ended|outcome|calmed|settled)\b/.test(combined)) {
    return 'How it ended';
  }

  return normalizeText(bucket.label);
};

const resolveBucketLabel = (bucket = {}, parentCategoryKey = '') => {
  const parentKey = normalizeParentCategoryKey(parentCategoryKey);
  const combined = normalizeKeyText(`${bucket.label} ${bucket.bucketType}`);

  if (/\b(action follow up|action|follow up|next step)\b/.test(combined)) {
    return 'Next step';
  }
  if (/\btrigger food\b|\bfood reaction\b/.test(combined)) {
    return 'Possible food reaction';
  }
  if (/\b(extraction issue|processing issue)\b/.test(combined)) {
    return isCaregiverConfirmationReason(bucket.summary || bucket.sourceText)
      ? 'One thing to confirm'
      : 'Related detail';
  }
  if (normalizeKeyText(bucket.label) === 'context') {
    return 'Related detail';
  }

  if (parentKey === 'medication') {
    return resolveMedicationBucketLabel(bucket);
  }
  if (parentKey === 'behavior') {
    return resolveBehaviorBucketLabel(bucket);
  }

  return normalizeText(bucket.label);
};

const resolveBucketDisplay = (bucket = {}) => {
  const bucketType = normalizeText(bucket.bucketType).toLowerCase();
  const categoryKey = resolveBucketCategoryKey(bucket);
  const logType = getLogTypeByCategory(categoryKey);
  const hasKnownCategory = categoryKey !== 'log' || bucketType === 'log';
  const palette = hasKnownCategory
    ? (logType.palette || DEFAULT_BUCKET_PALETTE)
    : DEFAULT_BUCKET_PALETTE;
  const IconComponent = hasKnownCategory && logType.iconComponent
    ? logType.iconComponent
    : ArticleOutlinedIcon;

  return {
    categoryKey,
    IconComponent,
    palette,
  };
};

const ContextSection = ({
  id,
  title,
  ariaLabel = '',
  count = null,
  emphasis = false,
  story = false,
  open,
  onToggle,
  children,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Button
      type="button"
      onClick={onToggle}
      aria-label={ariaLabel || undefined}
      aria-expanded={open}
      aria-controls={id}
      startIcon={open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
      sx={{
        width: '100%',
        minHeight: story ? 32 : 30,
        px: 0,
        py: 0.2,
        justifyContent: 'flex-start',
        color: TEXT_PRIMARY,
        fontSize: '0.76rem',
        fontWeight: 600,
        textTransform: 'none',
        '& .MuiButton-startIcon': {
          mr: 0.35,
          color: TEXT_MUTED,
          minWidth: 0,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.6}
        sx={{ minWidth: 0, flex: 1 }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: emphasis ? '0.7rem' : '0.76rem',
            fontWeight: 700,
            letterSpacing: emphasis ? '0.12em' : 0,
            textTransform: emphasis ? 'uppercase' : 'none',
            color: emphasis ? alpha(TEXT_MUTED, 0.92) : TEXT_PRIMARY,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {emphasis ? <Box sx={{ flex: 1, minWidth: 12 }} /> : null}
        {count !== null ? (
          <Chip
            label={count}
            size="small"
            sx={{
              height: 20,
              minWidth: 26,
              bgcolor: alpha(colors.neutral?.[200] || '#E5E7EB', 0.72),
              color: alpha(TEXT_MUTED, 0.92),
              border: 'none',
              fontSize: '0.68rem',
              fontWeight: 700,
              '& .MuiChip-label': {
                px: 0.85,
              },
            }}
          />
        ) : null}
      </Stack>
    </Button>
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box id={id} sx={{ pt: 0.15, pb: 0.55, minWidth: 0 }}>
        {children}
      </Box>
    </Collapse>
  </Box>
);

const PickedUpBucketRow = ({
  bucket = {},
  parentCategoryKey = '',
  allowRepeatingSummary = false,
  actionNode = null,
}) => {
  if (!actionNode && shouldHideBucketRow(bucket)) {
    return null;
  }

  const {
    IconComponent,
    palette,
  } = resolveBucketDisplay(bucket);
  const displayLabel = resolveBucketLabel(bucket, parentCategoryKey);
  const visibleSummary = getVisibleBucketSummary(bucket, { allowRepeatingSummary });

  return (
    <Box
      data-care-detail-category={resolveBucketCategoryKey(bucket)}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '42px minmax(0, 1fr)', sm: '42px minmax(0, 1fr)' },
        gap: { xs: 1, sm: 1 },
        alignItems: 'flex-start',
        py: { xs: 0.95, sm: 1 },
        borderBottom: `1px solid ${alpha(colors.semantic.line || '#CBD5E1', 0.58)}`,
        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: palette.strongBg || palette.softBg || '#E5E7EB',
          color: palette.text || TEXT_PRIMARY,
          mt: 0.05,
        }}
      >
        <IconComponent sx={{ fontSize: 19 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.6} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.86rem', lineHeight: 1.22, fontWeight: 700, color: TEXT_PRIMARY }}>
            {displayLabel}
          </Typography>
        </Stack>
        {visibleSummary ? (
          <Typography sx={{ mt: 0.28, fontSize: '0.8rem', lineHeight: 1.42, color: TEXT_MUTED, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {visibleSummary}
          </Typography>
        ) : null}
        {actionNode ? <Box sx={{ mt: visibleSummary ? 0.7 : 0.35 }}>{actionNode}</Box> : null}
      </Box>
    </Box>
  );
};

const PickedUpProcessingRow = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: { xs: 0.95, sm: 1 },
      color: TEXT_MUTED,
    }}
  >
    <CircularProgress size={16} thickness={4} sx={{ color: colors.semantic.sageDeep || TEXT_MUTED }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.25, fontWeight: 700, color: TEXT_PRIMARY }}>
        Updating details from your note
      </Typography>
      <Typography sx={{ mt: 0.2, fontSize: '0.76rem', lineHeight: 1.35, color: TEXT_MUTED }}>
        The details will appear here when they are ready.
      </Typography>
    </Box>
  </Box>
);

const CaptureFirstContextDetails = ({
  entryData = {},
  entryConfig = {},
  context = null,
  story = false,
  showOriginalNote = true,
  defaultDetailsOpen = false,
  parentCategoryKey = '',
  onAddDetails = null,
  onReviewMedication = null,
  onReviewProvider = null,
  medicationReviewNode = null,
}) => {
  const resolvedContext = context || getCaptureFirstTimelineContext(entryData, entryConfig);
  const [detailsOpen, setDetailsOpen] = React.useState(defaultDetailsOpen);
  const sectionId = React.useId();

  if (!resolvedContext?.hasVisibleContent) {
    return null;
  }

  const {
    isRefreshing,
    reviewReasons = [],
    reviewDestinations = {},
    storyBuckets = [],
    rawHints = [],
    originalNote = '',
    sourceAttachments = [],
    sourceNoteId = '',
    sourceNotePath = '',
    hasOriginalNote,
    hasSourceAttachments,
  } = resolvedContext;
  const shouldShowOriginalNote = showOriginalNote && (hasOriginalNote || hasSourceAttachments || sourceNoteId || sourceNotePath);

  const detailsId = `${sectionId}-capture-first-details-found`;
  const visibleStoryBuckets = storyBuckets.filter((bucket) => !shouldHideBucketRow(bucket));
  const clarificationBuckets = visibleStoryBuckets.filter((bucket) => (
    /\b(extraction issue|processing issue)\b/.test(
      normalizeKeyText(`${bucket.bucketType} ${bucket.label}`)
    )
  ));
  const careStoryBuckets = visibleStoryBuckets.filter(
    (bucket) => !clarificationBuckets.includes(bucket)
  );
  const medicationBucketIndex = careStoryBuckets.findIndex(
    (bucket) => resolveBucketCategoryKey(bucket) === 'medication'
  );
  const shouldShowRepeatingSummaries = careStoryBuckets.length > 0
    && careStoryBuckets.every((bucket) => !getVisibleBucketSummary(bucket));
  const confirmationReasons = reviewReasons.filter(isCaregiverConfirmationReason);
  const reviewSummary = formatTimelineReviewSummary({
    reviewDestinations,
    reviewReasons,
    parentCategoryKey,
  });
  const structuredReviewCount = Object.values(reviewDestinations)
    .reduce((total, count) => total + Number(count || 0), 0);
  const hasStructuredReview = structuredReviewCount > 0;
  const displayedConfirmationReasons = confirmationReasons.filter(
    (reason) => !hasStructuredReview || formatReviewReasonForCaregiver(reason) !== reviewSummary
  );
  const medicationReviewClarification = getMedicationReviewClarification(entryData);
  const medicationMatchingReasonPattern = /master medication|medication match|schedule/i;
  const medicationReviewStatus = entryData?.metadata?.medicationReview?.status
    || entryData?.metadata?.medicationAdministrationReview?.status;
  const medicationReviewComplete = ['resolved', 'dismissed', 'confirmed'].includes(medicationReviewStatus);
  const clarificationMessages = [...new Set([
    ...displayedConfirmationReasons.filter(
      (reason) => !(medicationReviewClarification || medicationReviewComplete)
        || !medicationMatchingReasonPattern.test(reason)
    ),
    ...clarificationBuckets.map((bucket) => bucket.summary || bucket.sourceText),
    medicationReviewClarification,
  ]
    .map(formatReviewReasonForCaregiver)
    .filter(Boolean))];
  const needsClarification = clarificationMessages.length > 0;
  const medicationReviewDetailCount = medicationReviewNode && medicationBucketIndex < 0 ? 1 : 0;
  const displayedDetailCount = story ? 0 : careStoryBuckets.length
    + medicationReviewDetailCount
    + (rawHints.length ? 1 : 0)
    + (isRefreshing ? 1 : 0);
  const hasDisplayDetails = displayedDetailCount > 0;
  const clarificationAction = getClarificationAction({
    entryData,
    entryConfig,
    parentCategoryKey,
    onAddDetails,
    onReviewMedication,
    onReviewProvider,
  });
  if (!hasStructuredReview && !needsClarification && !hasDisplayDetails && !shouldShowOriginalNote) {
    return null;
  }

  return (
    <Box
      data-testid="capture-first-context-details"
      sx={{
        mt: story ? 1.35 : 0.85,
        pt: story ? 0.95 : 0.65,
        mx: story ? { xs: -1.9, sm: -2.35 } : 0,
        px: story ? { xs: 1.9, sm: 2.35 } : 0,
        borderTop: `1px solid ${alpha(colors.semantic.line || '#CBD5E1', story ? 0.72 : 0.9)}`,
        bgcolor: story ? alpha(colors.semantic.sageMist || '#EEF5F1', 0.18) : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        gap: story ? 0.6 : 0.25,
        minWidth: 0,
      }}
    >
      {hasStructuredReview ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, pt: story ? 0.15 : 0 }}>
          <Chip
            icon={<WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />}
            label={reviewSummary}
            size="small"
            variant="outlined"
            sx={{
              height: 24,
              alignSelf: 'flex-start',
              color: REVIEW_AMBER,
              bgcolor: alpha(REVIEW_AMBER, 0.10),
              borderColor: alpha(REVIEW_AMBER, 0.24),
              fontSize: '0.68rem',
              fontWeight: 700,
              '& .MuiChip-icon': {
                color: REVIEW_AMBER,
                ml: 0.6,
              },
            }}
          />
        </Box>
      ) : null}

      {hasDisplayDetails ? (
        <ContextSection
          id={detailsId}
          title={`${displayedDetailCount} care detail${displayedDetailCount === 1 ? '' : 's'}`}
          ariaLabel={`${displayedDetailCount} care detail${displayedDetailCount === 1 ? '' : 's'}`}
          count={null}
          emphasis
          story={story}
          open={detailsOpen}
          onToggle={() => setDetailsOpen((current) => !current)}
        >
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            {isRefreshing ? <PickedUpProcessingRow /> : null}
            {careStoryBuckets.map((bucket, index) => (
              <PickedUpBucketRow
                key={`${bucket.label}-${index}`}
                bucket={bucket}
                parentCategoryKey={parentCategoryKey}
                allowRepeatingSummary={shouldShowRepeatingSummaries}
                actionNode={index === medicationBucketIndex ? medicationReviewNode : null}
              />
            ))}
            {medicationReviewDetailCount ? (
              <PickedUpBucketRow
                bucket={{ bucketType: 'medication', label: 'Medication', summary: '' }}
                parentCategoryKey={parentCategoryKey}
                actionNode={medicationReviewNode}
              />
            ) : null}
            {rawHints.length ? (
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.2, fontWeight: 800, color: TEXT_PRIMARY }}>
                  Other details
                </Typography>
                <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.45, color: TEXT_MUTED, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {rawHints.join('\n')}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </ContextSection>
      ) : null}

      {needsClarification ? (
        <TimelineClarificationSection
          messages={clarificationMessages}
          onAddDetails={clarificationAction.onClick}
          actionLabel={clarificationAction.label}
        />
      ) : null}

      {shouldShowOriginalNote ? (
        <SourceOriginalNotePanel
          originalNote={originalNote}
          sourceAttachments={sourceAttachments}
          sourceNoteId={sourceNoteId}
          sourceNotePath={sourceNotePath}
          showSourceReviewActions={false}
        />
      ) : null}
    </Box>
  );
};

export default React.memo(CaptureFirstContextDetails);
